import React from 'react';
import { FiCheckCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SignupStepIndicator from './auth/SignupStepIndicator';
import {
  ONBOARDING_STEPS,
  getOnboardingProgress,
  getCurrentStepId,
  getCompletedStepCount,
  isOnboardingComplete,
  isActivationComplete,
} from '../utils/onboarding';

const OnboardingChecklist = ({
  stats,
  modoOscuro,
  collapsed,
  onToggleCollapsed,
  onNavigate,
}) => {
  const progress = getOnboardingProgress(stats);
  const currentStepId = getCurrentStepId(progress);
  const completedCount = getCompletedStepCount(progress);
  const allDone = isOnboardingComplete(progress);
  const activationDone = isActivationComplete(progress);
  const activeStepNumber = allDone
    ? ONBOARDING_STEPS.length
    : ONBOARDING_STEPS.find((step) => step.id === currentStepId)?.step || 1;

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`} id="onboarding-checklist">
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <h2 className={`text-sm font-semibold ${textPri}`}>
            {allDone
              ? '¡Listo! Ya tenés movimientos cargados'
              : activationDone
                ? 'Ya ves quién te debe'
                : 'Primeros pasos'}
          </h2>
          <p className={`mt-0.5 text-xs ${textSec}`}>
            {allDone
              ? 'Circuito listo: registraste, cobraste y tu dashboard refleja el mes.'
              : activationDone
                ? 'Opcional: anotá un gasto para cerrar el circuito del mes. Abajo tenés la bandeja de cobro.'
                : `${completedCount} de ${ONBOARDING_STEPS.length} pasos. En ~15 min deberías ver impagas en Cobrar.`}
          </p>
        </div>
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
            modoOscuro
              ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {collapsed ? <FiChevronDown className="h-3.5 w-3.5" /> : <FiChevronUp className="h-3.5 w-3.5" />}
          {collapsed ? 'Mostrar' : 'Ocultar'}
        </button>
      </div>

      {!collapsed && (
        <div className={`border-t px-4 py-4 sm:px-5 ${modoOscuro ? 'border-gray-700/80' : 'border-gray-200'}`}>
          <SignupStepIndicator
            step={activeStepNumber}
            total={ONBOARDING_STEPS.length}
            labels={ONBOARDING_STEPS.map((step) => step.label)}
            modoOscuro={modoOscuro}
          />

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {ONBOARDING_STEPS.map((item) => {
              const done = progress[item.id];
              const isCurrent = !done && item.id === currentStepId;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onNavigate(item.href)}
                  className={`group rounded-xl border p-3 text-left transition-colors ${
                    done
                      ? modoOscuro
                        ? 'border-green-700/60 bg-green-950/20'
                        : 'border-green-200 bg-green-50/70'
                      : isCurrent
                        ? modoOscuro
                          ? 'border-[#1F80FF] bg-[#1F80FF]/10 ring-1 ring-[#1F80FF]/40'
                          : 'border-[#1F80FF] bg-blue-50/60 ring-1 ring-[#1F80FF]/30'
                        : modoOscuro
                          ? 'border-gray-700 hover:border-[#1F80FF] hover:bg-gray-800'
                          : 'border-gray-200 hover:border-[#1F80FF] hover:bg-blue-50/40'
                  }`}
                >
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-lg text-xs font-bold ${
                      done
                        ? 'bg-green-500/15 text-green-600'
                        : 'bg-[#1F80FF]/15 text-[#1F80FF]'
                    }`}
                  >
                    {done ? <FiCheckCircle className="h-4 w-4" /> : item.step}
                  </span>
                  <span className={`mt-2 block text-sm font-semibold ${textPri}`}>{item.label}</span>
                  <span className={`mt-0.5 block text-xs ${textMuted}`}>
                    {done ? 'Completado' : item.hint}
                  </span>
                  {isCurrent && (
                    <span className="mt-2 inline-flex rounded-full bg-[#1F80FF] px-2 py-0.5 text-[10px] font-semibold text-white">
                      Siguiente paso
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default OnboardingChecklist;
